import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PickersRangeCalendarHeaderProps } from '@mui/x-date-pickers-pro/PickersRangeCalendarHeader';
import {
  DateRange,
  DateRangePicker,
  DateRangeValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV2';
import { differenceInCalendarDays, endOfDay, startOfDay } from 'date-fns';

import { getLocale, translate } from '@/components/common/translateFunctions';
import { CustomCalendarHeader } from './CustomCalendarHeader';
import { localeText, shortcutsItems } from './DateRangeSelectBase';
import { isValidDate } from '@/utilities/dateUtils';

import './DateTimeSelect.css';

type DateRangeSelectProps = {
  setSelectedDates: (from: Date, to: Date) => void;
  currentValue: { from: Date | null; to: Date | null };
  disabled?: boolean;
  onSameRangeSelect?: () => void;
  /** Maximum number of selectable calendar dates, including the start date. */
  maxRangeDays?: number;
  /** Clears the previous end date when a new range is started from the calendar. */
  clearEndOnStartSelection?: boolean;
};

function exceedsMaxRangeDays(from: Date, to: Date, maxRangeDays: number | undefined): boolean {
  return maxRangeDays !== undefined && differenceInCalendarDays(to, from) >= maxRangeDays;
}

export function DateRangeSelect({
  setSelectedDates,
  currentValue,
  disabled = false,
  onSameRangeSelect,
  maxRangeDays,
  clearEndOnStartSelection = false,
}: DateRangeSelectProps) {
  const { t, i18n } = useTranslation();
  const acceptedValue = useRef<DateRange<Date> | undefined>(undefined);
  const hasReachedEndPosition = useRef(false);
  const hasCompletedRangeSelection = useRef(false);
  const hasAcceptedValue = useRef(false);
  const [draftValue, setDraftValue] = useState<{ from: Date | null; to: Date | null }>(currentValue);
  const [isEditing, setIsEditing] = useState(false);
  const [rangePosition, setRangePosition] = useState<'start' | 'end'>('start');
  const MINDATE = new Date('2010-01-01T00:00:00Z');
  const MAXDATE = new Date();
  const pickerValue = isEditing ? draftValue : currentValue;
  const isSelectingRangeEnd =
    clearEndOnStartSelection && isEditing && draftValue.from !== null && draftValue.to === null;

  function onAccept(newValue: DateRange<Date>, context: PickerChangeHandlerContext<DateRangeValidationError>) {
    const [selectedFrom, selectedTo] = newValue;

    if (!selectedFrom || !selectedTo) {
      return;
    }
    if (!isValidDate(selectedFrom) || !isValidDate(selectedTo)) {
      return;
    }

    let from: Date = selectedFrom;
    let to: Date = selectedTo;

    if (from < MINDATE) {
      from = MINDATE;
    }
    if (from > MAXDATE) {
      from = MAXDATE;
    }

    if (to < MINDATE) {
      to = MINDATE;
    }
    if (to > MAXDATE) {
      to = MAXDATE;
    }

    if (from > to) {
      [from, to] = [to, from];
    }

    if (exceedsMaxRangeDays(from, to, maxRangeDays)) {
      return;
    }

    const normalizedDate: [Date, Date] = [startOfDay(from), endOfDay(to)];
    hasAcceptedValue.current = true;
    setDraftValue({ from: normalizedDate[0], to: normalizedDate[1] });

    if (context.source === 'view') {
      // ha egerrel valasztott akkor egybol elsul
      setSelectedDates(normalizedDate[0], normalizedDate[1]);
    } else {
      // egyebkent csak eltesszuk
      acceptedValue.current = normalizedDate;
    }
  }

  function submit() {
    if (!acceptedValue.current || !acceptedValue.current[0] || !acceptedValue.current[1]) {
      return;
    }
    setSelectedDates(acceptedValue.current[0], acceptedValue.current[1]);
    setDraftValue({ from: acceptedValue.current[0], to: acceptedValue.current[1] });
    acceptedValue.current = undefined; // nem fogjuk 2x submit-olni
  }

  function onChange(newValue: DateRange<Date>, context: PickerChangeHandlerContext<DateRangeValidationError>) {
    const shouldClearPreviousEnd =
      clearEndOnStartSelection &&
      rangePosition === 'start' &&
      context.source === 'view' &&
      context.shortcut === undefined;

    setIsEditing(true);
    setDraftValue({ from: newValue[0], to: shouldClearPreviousEnd ? null : newValue[1] });
  }

  function handleOpen() {
    setIsEditing(true);
    if (clearEndOnStartSelection) {
      setRangePosition('start');
    }
    hasReachedEndPosition.current = false;
    hasCompletedRangeSelection.current = false;
    hasAcceptedValue.current = false;
  }

  function handleClose() {
    if (hasCompletedRangeSelection.current && !hasAcceptedValue.current) {
      onSameRangeSelect?.();
    }
    setIsEditing(false);
  }

  function handleRangePositionChange(nextRangePosition: 'start' | 'end') {
    if (clearEndOnStartSelection) {
      setRangePosition(nextRangePosition);
    }

    if (nextRangePosition === 'end') {
      hasReachedEndPosition.current = true;
      return;
    }

    if (hasReachedEndPosition.current) {
      hasCompletedRangeSelection.current = true;
    }
  }

  function shouldDisableDate(day: Date, position: 'start' | 'end'): boolean {
    if (position === 'start' || pickerValue.from === null) {
      return false;
    }

    return exceedsMaxRangeDays(pickerValue.from, day, maxRangeDays);
  }

  const locale = getLocale(i18n.language);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DateRangePicker
        closeOnSelect={true}
        className="mui-datetime-selector"
        value={[pickerValue.from, pickerValue.to]}
        onChange={onChange}
        onAccept={onAccept}
        onOpen={handleOpen}
        onClose={handleClose}
        onRangePositionChange={handleRangePositionChange}
        rangePosition={clearEndOnStartSelection ? rangePosition : undefined}
        disabled={disabled}
        minDate={MINDATE}
        shouldDisableDate={maxRangeDays === undefined ? undefined : shouldDisableDate}
        currentMonthCalendarPosition={2}
        disableFuture
        slots={{
          calendarHeader: CustomCalendarHeader,
        }}
        slotProps={{
          shortcuts: {
            id: 'shortcuts_panel',
            items: translate(shortcutsItems, t),
          },
          field: {
            id: 'date_picker_panel',
            onBlur: submit,
            onKeyDown: (event) => {
              if (event.code === 'Enter' || event.code === 'NumpadEnter') submit();
            },
          },
          textField: {
            placeholder: 'YYYY.MM.DD. - YYYY.MM.DD.',
            inputProps: { 'data-qa-element-id': 'single_date_input' },
            ...(isSelectingRangeEnd ? { error: false } : {}),
          },
          popper: {
            id: 'date_picker_popup',
          },
          openPickerButton: {
            id: 'open_calendar_picker_button',
          },
          day: (ownerState) => {
            const d = ownerState.day;
            const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            return {
              id: `day_of_month_${formatted}`,
              classes: maxRangeDays === undefined ? undefined : { day: 'date-range-select-visible-disabled-day' },
            };
          },
          calendarHeader: {
            maxDate: new Date(),
          } as Partial<PickersRangeCalendarHeaderProps>,
        }}
        localeText={localeText(t)}
      />
    </LocalizationProvider>
  );
}
